/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import React from 'react';
import { applyMiddleware, createStore, Store } from 'redux';
import { render } from 'react-dom';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import $ from 'jquery';
import { MetadataServiceComponent } from '../../adhoc/components/MetadataServiceComponent';
import rootReducer from '../../adhoc/reducers/index';
import initialStateFactory from '../../adhoc/store/initialState';
import {
    destroy, passProperties, refresh, resize
} from '../../adhoc/actions/apiActions';
import { IAdhocViewProperties, IRunOperation } from './AdHocView2Types';
import MetadataService from '../../adhoc/services/MetadataService';
import { IServices } from '../../adhoc/services/ServicesTypes';
import { QueryExecutionMultiLevelDataService as ActualMultiLevelDataService } from '../../adhoc/services/QueryExecutionMultiLevelDataService';
import { IState, IStateMetadataError } from '../../adhoc/store/types/StateTypes';
import { QueryExecutionMultiAxisDataService as ActualMultiAxisDataService } from '../../adhoc/services/QueryExecutionMultiAxisDataService';
import ValidatorFacade from '../../adhoc/validators/ValidatorFacade';
import { IPropertiesValidator } from '../../adhoc/validators/ValidatorTypes';
import adhocSettings from '../../adhoc/adhocSettings'
import EventComponent from '../../adhoc/components/EventComponent';

function getAllowedTypesList(state: IState) {
    return state.visualizationTypesRegistry.filter((visualizationType) => visualizationType.available === true)
        .map((type) => type.descriptor.name);
}

function getInputParameters(state: IState) {
    return state.filterParameters.map((parameter: any) => ({
        id: parameter.name,
        type: parameter.type
    }))
}

function getOutputParameters(state: IState) {
    const { rows, columns } = state.levelsLayout;
    let hasMeasures = false;

    function outparametersCallBackFn() {
        return (acc: any, item: any) => {
            if (item.kind === 'measure' && !hasMeasures) {
                acc.push({ id: 'Measures', label: 'Measures' });
                hasMeasures = true;
            } else if (item.kind === 'level') {
                acc.push({
                    id: item.id,
                    type: item.type,
                    label: item.label
                })
            }
            return acc;
        };
    }

    const outputParameters = rows.reduce(outparametersCallBackFn(), []).concat(columns.reduce(outparametersCallBackFn(), []));
    // move Measures as a first element in array
    if (outputParameters && outputParameters.length) {
        outputParameters.unshift(outputParameters.splice(outputParameters.findIndex((item: any) => item.id === 'Measures'), 1)[0]);
    }
    return outputParameters;
}

function getCurrentVisualizationType(state: IState) {
    return state.visualizationDescriptor ? state.visualizationDescriptor.name : '';
}

function toSuccessCallbackObject(state: IState) {
    return {
        metadata: {
            currentVisualizationType: getCurrentVisualizationType(state),
            availableVisualizationTypes: getAllowedTypesList(state),
            inputParameters: getInputParameters(state),
            outputParameters: getOutputParameters(state)
        }
    };
}

function toErrorCallbackObject(error: IStateMetadataError) {
    return error;
}

export default class AdHocView {
    store: Store;

    container: Element;

    runQueue: Array<IRunOperation>;

    services: IServices;

    validator: IPropertiesValidator;

    constructor(props: IAdhocViewProperties, options: any) {
        const thunkModule: any = thunk;
        const thunkFixed = thunkModule.default ? thunkModule.default : thunk;
        const initialState = initialStateFactory();

        this.runQueue = [];
        this.services = {
            metadataService: new MetadataService(),
            multiLevelDataService: new ActualMultiLevelDataService(),
            multiAxisDataService: new ActualMultiAxisDataService()
        };

        [this.container] = $(props.properties.container);

        const height = $(this.container).height();
        const width = $(this.container).width();

        if (height) {
            initialState.adHocViewHeight = height;
        }

        if (width) {
            initialState.adHocViewWidth = width;
        }

        if (props.properties.allowDrilldown === false) {
            initialState.drillDownAllowed = false;
        }

        const storeEnhancer = applyMiddleware(thunkFixed.withExtraArgument(this.services));
        this.store = createStore(rootReducer, initialState, storeEnhancer);

        this.validator = new ValidatorFacade(this.store);

        render(
            <Provider store={this.store}>
                <MetadataServiceComponent validator={this.validator} />
                <EventComponent stateModel={options.stateModel} />
            </Provider>,
            this.container
        );

        if ($(this.container).find('.jr-jAdhocVizualization').length) {
            $(this.container).parents('#embedvizcode').find(' #embedDrawer .embed button').removeAttr('disabled');
        }
    }

    run(dfd: JQuery.Deferred<any>, props: IAdhocViewProperties) {
        dfd.always(() => {
            this.proceedWithRun();
        });

        this.runQueue.push({ dfd, props });

        this.proceedWithRun();
    }

    refresh(dfd: JQuery.Deferred<any>) {
        dfd.always(() => {
            this.proceedWithRun();
        });

        this.runQueue.push({ dfd, refresh: true });

        this.proceedWithRun();
    }

    destroy(dfd: JQuery.Deferred<any>) {
        dfd.always(() => {
            this.proceedWithRun();
        });

        this.runQueue.push({ dfd, destroy: true });

        this.proceedWithRun();
    }

    resize(dfd: JQuery.Deferred<any>) {
        const height = $(this.container).height() || adhocSettings.DEFAULT_HEIGHT;
        const width = $(this.container).width() || adhocSettings.DEFAULT_WIDTH;
        this.store.dispatch(resize(width, height));
    }

    proceedWithRun() {
        const operation: IRunOperation | null = this.runQueue.shift() || null;

        if (operation !== null) {
            const deferredAction = $.Deferred();
            const actions = [deferredAction];

            if (operation.props) {
                const error = this.validator.validate(operation.props.properties);
                if (error) {
                    operation.dfd.reject(toErrorCallbackObject(error));
                } else {
                    this.store.dispatch(passProperties(operation.props.properties, deferredAction));
                }
            } else if (operation.refresh) {
                actions.push(this.services.metadataService.reset());
                actions.push(this.services.multiAxisDataService.reset());
                actions.push(this.services.multiLevelDataService.reset());
                this.store.dispatch(refresh(deferredAction));
            } else if (operation.destroy) {
                actions.push(this.services.metadataService.reset());
                actions.push(this.services.multiAxisDataService.reset());
                actions.push(this.services.multiLevelDataService.reset());
                this.store.dispatch(destroy(deferredAction));
            }

            const actionsDfd = actions.length > 1 ? $.when(...actions) : deferredAction;
            actionsDfd.done(() => {
                const data = toSuccessCallbackObject(this.store.getState());
                if (operation.props) {
                    operation.props.data = data;
                }
                operation.dfd.resolve(data);
            }).fail(() => {
                const state = this.store.getState();
                let error = state.metadataError;
                if (!error) {
                    error = state.dataError;
                }

                operation.dfd.reject(toErrorCallbackObject(error));
            });
        }
    }
}
